/**
 * Utility functions for handling file operations in Panel system
 */

export interface FileContent {
  name: string
  content: string
  language?: string
  mimeType?: string
  size?: number
}

/**
 * Attempts to fetch file content from various sources
 */
export async function fetchFileContent(file: {
  name: string
  url?: string
  downloadUrl?: string
  mimeType?: string
  size?: number
}): Promise<FileContent> {
  const fileUrl = file.downloadUrl || file.url

  if (!fileUrl) {
    return {
      name: file.name,
      content: 'No content available - file URL not provided',
      mimeType: file.mimeType,
      size: file.size,
    }
  }

  try {
    // For text-based files, try to fetch and display content
    if (isTextFile(file.mimeType)) {
      const response = await fetch(fileUrl)

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`)
      }

      const content = await response.text()

      return {
        name: file.name,
        content,
        language: getLanguageFromFile(file.name, file.mimeType),
        mimeType: file.mimeType,
        size: file.size,
      }
    }

    // For binary files, show metadata instead
    return {
      name: file.name,
      content: `File Type: ${file.mimeType || 'Unknown'}\nSize: ${formatFileSize(file.size)}\n\nBinary file content cannot be displayed as text.\nUse the download button to save this file.`,
      mimeType: file.mimeType,
      size: file.size,
    }
  } catch (error) {
    console.error('Error fetching file content:', error)

    return {
      name: file.name,
      content: `Error loading file content: ${error instanceof Error ? error.message : 'Unknown error'}\n\nFile may be inaccessible or require authentication.`,
      mimeType: file.mimeType,
      size: file.size,
    }
  }
}

/**
 * Determines if a file type is likely to be text-based and displayable
 */
function isTextFile(mimeType?: string): boolean {
  if (!mimeType) return false

  const textTypes = [
    'text/',
    'application/json',
    'application/javascript',
    'application/xml',
    'application/csv',
    'application/sql',
    'application/x-sh',
    'application/x-python',
  ]

  return textTypes.some((type) => mimeType.includes(type))
}

/**
 * Determines syntax highlighting language from file extension and MIME type
 */
function getLanguageFromFile(fileName: string, mimeType?: string): string | undefined {
  const extension = fileName.toLowerCase().split('.').pop()

  // Map extensions to language identifiers
  const extensionMap: Record<string, string> = {
    js: 'javascript',
    jsx: 'jsx',
    ts: 'typescript',
    tsx: 'tsx',
    py: 'python',
    rb: 'ruby',
    php: 'php',
    java: 'java',
    c: 'c',
    cpp: 'cpp',
    h: 'c',
    css: 'css',
    scss: 'scss',
    sass: 'sass',
    less: 'less',
    html: 'html',
    xml: 'xml',
    json: 'json',
    yaml: 'yaml',
    yml: 'yaml',
    md: 'markdown',
    sql: 'sql',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    fish: 'bash',
  }

  if (extension && extensionMap[extension]) {
    return extensionMap[extension]
  }

  // Fallback to MIME type mapping
  if (mimeType) {
    if (mimeType.includes('javascript')) return 'javascript'
    if (mimeType.includes('json')) return 'json'
    if (mimeType.includes('xml')) return 'xml'
    if (mimeType.includes('css')) return 'css'
    if (mimeType.includes('html')) return 'html'
    if (mimeType.includes('python')) return 'python'
  }

  return 'text'
}

/**
 * Format file size for display
 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown size'

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}
